import { useEffect, useId, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useLocation } from "react-router";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import dayjs from "dayjs";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import DashboardPaper from "../../components/common/DashboardPaper";
import { useApi } from "../../hooks/useApi";
import { useLatest } from "../../hooks/useLatest";
import { useRefreshCompanies } from "../../hooks/useRefreshCompanies";
import { useCompanySwitch, useSelectCompany } from "../../hooks/useCompanyId";
import type { BillingProfileOption, OnboardingAnswers, OnboardingCreated, OnboardingDetail, OnboardingKind, OnboardingPrefill } from "../../types/onboarding";
import { useOnboardingPreview } from "./useOnboardingPreview";
import { useOpenCompanyOnboarding } from "./useOpenCompanyOnboarding";
import ClientSection from "./ClientSection";
import BrandsGrid from "./BrandsGrid";
import ServicesSection from "./ServicesSection";
import StackSection from "./StackSection";
import ReviewSection from "./ReviewSection";
import SectionNav from "./SectionNav";
import { listMissing, SECTIONS, type Missing, type SectionKey } from "./validation";
import { toOtherProcessorNames } from "./labels";
import { EMPTY_STACK } from "./stack";

const withCoveredInUse = (a: OnboardingAnswers): OnboardingAnswers => {
  const names = toOtherProcessorNames(a.brands);
  return {
    ...a,
    ...(a.representmentProcessors && { representmentProcessors: a.representmentProcessors.filter((p) => a.brands.some((b) => b.processors.includes(p))) }),
    ...(a.representmentOtherNames && { representmentOtherNames: a.representmentOtherNames.filter((name) => names.includes(name)) }),
  };
};

const emptyAnswers = (isExisting: boolean): OnboardingAnswers => ({
  companyName: "",
  legalEntity: "",
  signedOn: isExisting ? "" : dayjs.utc().format("YYYY-MM-DD"),
  admin: { firstName: "", lastName: "", email: "" },
  brands: isExisting ? [] : [{ name: "", processors: [], isNew: true, stack: EMPTY_STACK }],
  alerts: { enabled: false, networks: [] },
  representment: false,
  addOns: "",
  switching: { enabled: false, someMidsNew: false },
  salesNotes: "",
});

// With onGo the list spans sections and each entry opens its own; without it, it lists the open section's.
function MissingList({ items, onGo }: { items: Missing[]; onGo?: (section: SectionKey) => void }) {
  const headingId = useId();
  return (
    <Alert severity="warning">
      <Typography id={headingId} variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Fill these in first</Typography>
      <ul aria-labelledby={headingId} className="flex flex-col gap-0.5 text-sm">
        {items.map((m) => (
          <li key={`${m.section}:${m.label}`}>
            {onGo ? (
              <button type="button" onClick={() => onGo(m.section)} className="cursor-pointer text-left underline-offset-2 hover:underline">
                {m.label} <span className="text-gray-500">· {SECTIONS.find((s) => s.key === m.section)?.label}</span>
              </button>
            ) : m.label}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// A fresh form per address: going from one company's form to another, or to a new client, must not
// carry the previous answers over.
export default function OnboardingNew() {
  const { pathname } = useLocation();
  return <OnboardingForm key={pathname} />;
}

function OnboardingForm() {
  const navigate = useNavigate();
  const params = useParams();
  const companyId = params.companyId ? Number(params.companyId) : null;
  const editingId = params.onboardingId ? Number(params.onboardingId) : null;
  const isEditing = editingId !== null;
  const [kind, setKind] = useState<OnboardingKind | null>(null);
  const [answersVersion, setAnswersVersion] = useState(0);
  const [editedCompanyId, setEditedCompanyId] = useState<number | null>(null);
  const isExisting = companyId !== null || kind === "existing_company";
  const needsLoad = companyId !== null || isEditing;
  const { get } = useApi();
  const { post, put } = useApi();
  const refreshCompanies = useRefreshCompanies();
  const [formAnswers, setAnswers] = useState<OnboardingAnswers>(() => emptyAnswers(isExisting));
  // Coverage ticks stay in the form while their processor is off a brand; only processors in use are sent.
  const answers = useMemo(() => withCoveredInUse(formAnswers), [formAnswers]);
  const [prefilled, setPrefilled] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [openOnboardingId, setOpenOnboardingId] = useState<number | null>(null);
  const [section, setSection] = useState(0);
  // Sections the user has left or jumped past: only those show what's missing, so a blank form starts quiet.
  // Editing counts every section as attempted; saved answers were valid, so it still starts quiet.
  const [attempted, setAttempted] = useState<ReadonlySet<SectionKey>>(() => new Set(isEditing ? SECTIONS.map((s) => s.key) : []));
  // What the form shows as missing is taken when a field is left or the user moves on, never while typing; a tick only clears it.
  const [shown, setShown] = useState<Missing[]>([]);
  const [billingProfiles, setBillingProfiles] = useState<BillingProfileOption[]>([]);
  const [billingProfileIdByBrand, setBillingProfileIdByBrand] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { preview, error: previewError } = useOnboardingPreview(answers, companyId ?? (kind === "existing_company" ? editedCompanyId ?? undefined : undefined));

  const latestAnswers = useLatest(formAnswers);
  const set = (patch: Partial<OnboardingAnswers>) => {
    latestAnswers.current = { ...latestAnswers.current, ...patch };
    setAnswers(latestAnswers.current);
  };

  useEffect(() => {
    if (editingId === null) return;
    get<OnboardingDetail>(`/api/onboardings/${editingId}`)
      .then(async (detail) => {
        // A brand added while editing still needs a billing profile when the company has several.
        const prefill = await get<OnboardingPrefill>(`/api/onboardings/prefill/${detail.companyId}`);
        setAnswers(detail.answers);
        setAnswersVersion(detail.answersVersion);
        setKind(detail.kind);
        setEditedCompanyId(detail.companyId);
        setBillingProfiles(prefill.billingProfiles);
        setBillingProfileIdByBrand(prefill.billingProfileIdByBrand);
        setPrefilled(true);
      })
      .catch((err: Error) => setPrefillError(err.message));
  }, [editingId, get]);

  useEffect(() => {
    if (companyId === null) return;
    get<OnboardingPrefill>(`/api/onboardings/prefill/${companyId}`)
      .then((prefill) => {
        setAnswers((prev) => ({ ...prev, ...prefill.answers }));
        setBillingProfiles(prefill.billingProfiles);
        setBillingProfileIdByBrand(prefill.billingProfileIdByBrand);
        setOpenOnboardingId(prefill.openOnboardingId);
        setPrefilled(true);
      })
      .catch((err: Error) => setPrefillError(err.message));
  }, [companyId, get]);

  useSelectCompany(companyId ?? editedCompanyId);
  const openCompanyOnboarding = useOpenCompanyOnboarding();
  // The form follows the header to the picked company's fresh form; editing leaves like the checklist does.
  useCompanySwitch(companyId ?? editedCompanyId, (picked) => {
    if (isEditing) void openCompanyOnboarding(picked);
    else if (picked !== null) navigate(`/onboarding/new/${picked}?companyId=${picked}`);
  });

  const canCreate = shown.length === 0 && (!needsLoad || prefilled) && !submitting;
  const current = SECTIONS[section];
  const refresh = () => {
    const missing = listMissing(latestAnswers.current, isExisting, billingProfiles);
    setShown(missing);
    return missing;
  };
  // Ticking Other reveals its name field; flagging that before anything is typed reads as an error.
  const clearResolved = () => {
    const missing = listMissing(latestAnswers.current, isExisting, billingProfiles);
    setShown((prev) => missing.filter((m) => prev.some((p) => p.section === m.section && p.label === m.label)));
  };
  const open = (index: number) => {
    setAttempted((prev) => new Set([...prev, ...SECTIONS.slice(0, index).map((s) => s.key)]));
    refresh();
    setSection(index);
  };
  const goToSection = (key: SectionKey) => open(SECTIONS.findIndex((s) => s.key === key));
  // Editing saves from any section, so every section lists all that blocks saving.
  const sectionMissing = isEditing ? shown : attempted.has(current.key) ? shown.filter((m) => m.section === current.key) : [];
  const missingFields = new Set(sectionMissing.flatMap((m) => (m.field ? [m.field] : [])));

  const create = async () => {
    if (refresh().length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (isEditing) {
        await put(`/api/onboardings/${editingId}/answers`, { answers, answersVersion });
        await refreshCompanies();
        navigate(`/onboarding/${editingId}`);
        return;
      }
      const created = await post<OnboardingCreated>("/api/onboardings", { companyId: companyId ?? undefined, answers });
      await refreshCompanies();
      navigate(`/onboarding/${created.onboardingId}`);
    } catch (err) {
      setSubmitError((err as Error).message);
      setSubmitting(false);
    }
  };

  const title = isEditing ? "Edit answers" : isExisting ? "Existing company" : "New client";

  return (
    <div>
      <PageMeta title={`Chargemont | ${title}`} description="Start a client onboarding checklist" />
      <PageContainer>
        <PageHeader title={title} />
        {prefillError && <Alert severity="error" sx={{ mb: 3 }}>Could not load the company: {prefillError}</Alert>}
        {needsLoad && !prefilled && !prefillError && <CircularProgress size={24} />}

        {needsLoad && !prefilled ? null : openOnboardingId !== null ? (
          <Alert
            severity="info"
            action={<Button component={RouterLink} to={`/onboarding/${openOnboardingId}`} color="inherit" size="small">Open onboarding</Button>}
          >
            This company already has an onboarding in progress
          </Alert>
        ) : (
        <div className="flex flex-wrap items-start gap-5">
          <SectionNav section={section} attempted={attempted} shown={shown} stepCount={preview?.items.length} onOpen={open} />

          <DashboardPaper sx={{ flex: "999 1 560px", minWidth: 0 }}>
            <div className="border-b border-gray-200 px-6 py-5 dark:border-gray-800">
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>{current.label}</Typography>
              <Typography variant="body2" color="text.secondary">{current.hint}</Typography>
            </div>
            <div
              className="flex flex-col gap-5 p-6"
              onBlur={(e) => e.target instanceof Element && e.target.matches("input:not([type=checkbox]), textarea, [role=combobox]") && refresh()}
              onFocus={(e) => e.target instanceof Element && e.target.matches("[role=combobox], .MuiInputAdornment-root button") && refresh()}
              onChange={(e) => e.target instanceof Element && e.target.matches("input[type=checkbox]") && clearResolved()}
            >
              {current.key !== "review" && submitError && <Alert severity="error">{submitError}</Alert>}
              {current.key !== "review" && sectionMissing.length > 0 && <MissingList items={sectionMissing} onGo={isEditing ? goToSection : undefined} />}
              {current.key === "client" && (
                <ClientSection answers={answers} set={set} companyLocked={isExisting} agreementOptional={isExisting} missingFields={missingFields} />
              )}
              {current.key === "brands" && (
                <BrandsGrid
                  answers={answers} set={set} isExisting={isExisting} editing={isEditing} missingFields={missingFields}
                  billingProfiles={billingProfiles} billingProfileIdByBrand={billingProfileIdByBrand} onRemoved={refresh}
                />
              )}
              {current.key === "services" && <ServicesSection answers={formAnswers} set={set} />}
              {current.key === "stack" && <StackSection answers={answers} set={set} missingFields={missingFields} />}
              {current.key === "review" && (
                <ReviewSection answers={answers} set={set} preview={preview} previewError={previewError} submitError={submitError} />
              )}
              {current.key === "review" && shown.length > 0 && (
                <MissingList items={shown} onGo={goToSection} />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
              <Button variant="outlined" disabled={section === 0} onClick={() => setSection(section - 1)}>Back</Button>
              {current.key === "review" ? (
                <Button variant="contained" disabled={!canCreate} onClick={create}>
                  {isEditing ? "Save answers" : isExisting ? "Start onboarding" : "Create client"}
                </Button>
              ) : (
                <div className="flex gap-3">
                  {isEditing && <Button variant="outlined" disabled={!canCreate} onClick={create}>Save answers</Button>}
                  <Button variant="contained" onClick={() => open(section + 1)}>Next</Button>
                </div>
              )}
            </div>
          </DashboardPaper>
        </div>
        )}
      </PageContainer>
    </div>
  );
}
